// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the same domain and port as the frontend
  return `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const requestFile = async (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  var requestOptions = {
    method: 'POST',
    body: body,
    redirect: 'follow',
  };

  try {
    const response = await fetch(url, requestOptions);
    const text = await response.text();
    // Do not throw here; return status and text so caller can decide about retries
    return { status: response.status, text };
  } catch (error) {
    console.log('error', error);
    return { status: 0, text: JSON.stringify({ error: error?.message || 'network_error' }) };
  }
};

// post for file
const postFile = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: 'POST',
    body,
  };

  return requestFile(path, requestOptions);
};

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const post = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/transit+json',
    },
    body: serialize(body),
  };
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

export const chatbotContact = body => {
  return post('/api/contact-widget', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// SendGrid Email
export const sendGridEmail = (body) => {
  return post('/api/sendGridEmail', body);
};

// Collection API's
export const insertCollection = (body) => {
  return post('/api/insert-collection', body);
};
export const updateCollection = (body) => {
  return post('/api/update-collection', body);
};
export const getCollection = (body) => {
  return post('/api/get-collection', body);
};
export const deleteCollection = (body) => {
  return post('/api/delete-collection', body);
};
export const updateWalletFromBackend = (body) => {
  return post('/api/updateWalletFromBackend', body);
};
export const generateInvoice = body => {
  // Use plain JSON (not Transit) so nested address fields are preserved
  const url = `${apiBaseUrl()}/api/generateInvoice`;
  return window.fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(res => res.json());
};

// Algolia search data
export const createAlgoliaData = body => {
  return post('/api/createAlgoliaData', body);
};
export const updateAlgoliaData = body => {
  return post('/api/updateAlgoliaData', body);
};
export const deleteAlgoliaData = body => {
  return post('/api/deleteAlgoliaData', body);
};
export const searchAlgoliaData = body => {
  return post('/api/searchAlgoliaData', body);
};

// closeListing
export const closeListing = body => {
  return post('/api/closeListing', body);
};

// updateListing 
export const updateListing = body => {
  return post('/api/updateListing', body);
};

// updateProfile 
export const isdkUpdateProfile = body => {
  return post('/api/updateProfile', body);
};

export const getListingInfo = body => {
  return post('/api/userInfoThroughListing', body);
};

// Create PDF from Html
export const createResume = body => {
  return post('/api/createResume', body);
};

export const mailForCreateListing = body => {
  return post('/api/mailForCreateListing', body);
};

export const pauseReminderNotifyToUser = body => {
  return post('/api/pauseReminderNotifyToUser', body);
};

// Pipeline Api's
// Person
export const createPersonApi = body => {
  return post('/api/createPersonApi', body);
};

export const updatePipedrivePerson = body => {
  return post('/api/updatePersonApi', body);
};
// Deals
export const createDealApi = body => {
  return post('/api/createDeal', body);
};

export const updatePipedriveDeal = body => {
  return post('/api/updateDeal', body);
};

export const closeDeal = body => {
  return post('/api/deleteDeal', body);
};

export const updateTransaction = body => {
  return post('/api/updateTransaction', body);
};

export const changeDealStatus = body => {
  return post('/api/changeDealStatus', body);
};

export const updateUserListing = body => {
  return post('/api/updateUserListing', body)
}

export const updateUserProfile = body => {
  return post('/api/updateUserProfile', body)
}


export const transactionHistory = body => {
  return post('/api/transaction-history', body)
}

export const insertFollowers = body => {
  return post('/api/insert-followers', body)
}

export const getFollowers = body => {
  return post('/api/get-followers', body)
}

export const updateFollowers = body => {
  return post('/api/update-followers', body)
}

export const getParentJobRole = body => {
  return post('/api/get-parent-job-roles', body)
}
export const getfetchAIResumeUpdate = body => {
  return postFile('/api/get-fetch-AI-ResumeUpdate', body)
}

export const personDetection = body => {
  return post('/api/person-detection', body);
};

export const createOrUpdateListing = body => {
  return post('/api/createOrUpdateListing', body);
};

export const saveResumeUrlMongoDb = body => {
  return post('/api/save-resume-url-mongodb', body);
};


export const updateMetaDataApi = body => {
  return post('/api/updateMetaDataApi', body);
};

export const scheduleDay3SignupEmail = body => {
  return post('/api/scheduleDay3SignupEmail', body);
};

export const cancelProSignupEmails = body => {
  return post('/api/cancelProSignupEmails', body);
};

export const scheduleHalfwayProfileEmail = body => {
  return post('/api/scheduleHalfwayProfileEmail', body);
};

export const scheduleInitialSearchEmail = body => {
  return post('/api/scheduleInitialSearchEmail', body);
};

export const updateListingMetaDataApi = body => {
  return post('/api/updateListingMetaDataApi', body);
};

export const reportCandidateEmail = body => {
  return post('/api/report-candidate', body);
};

export const humanImageDetection = body => {
  return post('/api/human-image', body);
};


export const notifyUsers = body => {
  return post('/api/notify-entities', body);
};

export const policyUrlMongoDb = body => {
  return post('/api/save-policy-url-mongodb', body);
};

export const createPolicyPdf = body => {
  return post('/api/create-policy-pdf', body);
};

export const chatGPTResponse = body => {
  return post('/api/generate-company-description', body);
};

export const chatGptQuestionsQuery = body => {
  return post('/api/generate-interview-questions', body);
};


export const saveMarketingData = body => {
  return post('/api/save-marketing-data-mongo', body);
};


export const marketingEmail = body => {
  return post('/api/marketing-email', body);
};

export const fetchMatchedListingsBatch = body => {
  return post('/api/matched-listings-batch', body);
};


export const saveNewsletter = body => {
  return post("/api/saveNewsletterData", body)
}


export const createBlog = body => {
  return post("/api/create-blog", body)
}

export const editBlog = body => {
  return post("/api/edit-blog", body)
}

export const deleteBlog = body => {
  return post("/api/delete-blog", body)
}

export const getBlogsAdmin = body => {
  return post("/api/get-blog-admin", body)
}

export const getBlogByIdAdmin = body => {
  return post("/api/get-blog-by-id-admin", body)
}

export const getBlogsUser = body => {
  return post("/api/get-blog-user", body)
}

export const getBlogByIdUser = body => {
  return post("/api/get-blog-by-id-user", body)
}

export const verifyRecaptchaApi = async (body) => {
  return post("/api/verify-recaptcha", body);
};

export const reportTracking = async (body) => {
  return post("/api/report-tracking", body);
};

export const getAllUsers = async (body) => {
  return post("/api/get-users", body);
};

export const trackEventsMongo = async (body) => {
  return post("/api/track-events-mongo", body);
};


export const fetchGuestsUsers = async (body) => {
  return post("/api/fetchGuestsUsers", body);
};


export const getVlogs = body => {
  return post("/api/get-vlogs", body)
}

export const getFeaturedVlog = body => {
  return post("/api/get-featured-vlog", body)
}

export const createVlogs = body => {
  return post("/api/create-vlog", body)
}

export const updateVlog = body => {
  return post("/api/update-vlog", body)
}

export const deleteVlog = body => {
  return post("/api/delete-vlog", body)
}

export const getHeroContent = body => {
  return post("/api/get-hero-content", body)
}

export const updateHeroContent = body => {
  return post("/api/update-hero-content", body)
}


// Forteh Blog Page Content APIs
export const getBlogPageContent = body => {
  return post('/api/get-blog-page-content', body);
};

export const saveBlogPageContent = body => {
  return post('/api/save-blog-page-content', body);
};

export const getAllFortehBlogs = body => {
  return post('/api/get-all-forteh-blogs', body);
};

export const deleteFortehBlog = body => {
  return post('/api/delete-forteh-blog', body);
};
// MailerLite subscriber sync
export const mailerLiteSyncSignup = body => {
  return post('/api/mailerLite/sync-signup', body);
};

export const mailerLiteSyncRole = body => {
  return post('/api/mailerLite/sync-role', body);
};

export const mailerLiteSyncCompanyRole = body => {
  return post('/api/mailerLite/sync-company-role', body);
};
